import { Box, FormControl, MenuItem, Select, SelectChangeEvent, styled, Typography, Tooltip } from '@mui/material';
import { useState } from 'react';
import { DashboardTile } from './DashboardTile';
import { useTenants } from '@/services/tenants/useTenants';
import { Api, MonitorHeart, TravelExplore } from '@mui/icons-material';
import { BodyText } from '@/components/BodyText';
import { InternalOnlyBadge } from '@/components/InternalOnlyBadge';
import { DailyHealthStatus, SystemHealthResponse } from '@/api/generatedApi';
import {
  useGetSystemHealth,
  DEFAULT_HEALTH_PERIOD,
  HEALTH_PERIOD_OPTIONS,
  HealthPeriodDays,
} from '@/hooks/api/system-health/useGetSystemHealth';

// --- Styled Components ---

const StatusTimeline = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const TimelineBarContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  width: '100%',
  height: '20px',
});

const DayBar = styled(Box, { shouldForwardProp: property => property !== 'healthStatus' })<{
  healthStatus: DailyHealthStatus;
}>(({ theme, healthStatus }) => {
  let color = theme.palette.success.main;
  if (healthStatus === DailyHealthStatus.Degraded) {
    color = theme.palette.warning.main;
  } else if (healthStatus === DailyHealthStatus.MajorOutage) {
    color = theme.palette.error.main;
  }

  return {
    flex: 1,
    height: '100%',
    borderRadius: '3px',
    backgroundColor: color,
    minWidth: '3px',
    transition: 'opacity 0.15s ease',
    '&:hover': {
      opacity: 0.8,
    },
  };
});

const TimelineFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.text.secondary,
}));

const MetricCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '12px 14px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.default,
}));

const IconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

const CardHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
});

const HeadlineRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
});

const DetailText = styled(BodyText)({
  fontSize: '0.75rem',
  lineHeight: 1.2,
});

// --- Helper Functions ---

const getUptimeColor = (availability?: number) => {
  if (availability === undefined) return 'text.secondary';
  if (availability < 0.999) return 'warning.main';
  return 'success.main';
};

// A missing figure is shown as an em-less placeholder rather than 0%, which would read as an outage.
const formatAvailability = (value?: number) => {
  return value === undefined ? 'No data' : `${(value * 100).toFixed(2)}%`;
};

const formatDayDate = (isoDate: string) => {
  return new Date(isoDate).toLocaleDateString(undefined, { timeZone: 'UTC' });
};

// --- Tile Content Component ---

const SystemHealthContent = ({ data, days }: { data: SystemHealthResponse; days: HealthPeriodDays }) => {
  const { platform } = data;
  const platformUptimeColor = getUptimeColor(platform.overallAvailability);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* --- Platform Daily Timeline --- */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Typography
            variant='caption'
            sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            Platform ({globalThis.location.hostname})
          </Typography>
          <BodyText fontSize='0.875rem' fontWeight={600} color={platformUptimeColor}>
            {formatAvailability(platform.overallAvailability)} uptime
          </BodyText>
        </Box>

        <StatusTimeline>
          <TimelineBarContainer title={`Daily Platform Availability (Last ${days} Days)`}>
            {platform.dailyTimeline.map(day => {
              const incident = day.incidentDescription ? ` (${day.incidentDescription})` : '';
              const reading =
                day.status === DailyHealthStatus.NoData ? 'no canary score' : formatAvailability(day.availability);
              const title = `${formatDayDate(day.date)} - ${reading}${incident}`;
              return (
                <Tooltip key={day.date} title={title} placement='top'>
                  <DayBar healthStatus={day.status} data-testid='system-health-day-bar' />
                </Tooltip>
              );
            })}
          </TimelineBarContainer>
          <TimelineFooter>
            <span>{days - 1} days ago</span>
            <span>Today</span>
          </TimelineFooter>
        </StatusTimeline>
      </Box>

      {/* --- Platform check breakdown --- */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography
          variant='caption'
          sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          Platform Metrics
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
          <MetricCard>
            <CardHeaderRow>
              <IconBox>
                <Api fontSize='small' />
              </IconBox>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                <BodyText fontSize='0.8125rem' fontWeight={500} noWrap>
                  API checks passed
                </BodyText>
                <DetailText color='text.secondary'>All synthetic canaries</DetailText>
              </Box>
            </CardHeaderRow>
            <HeadlineRow>
              <BodyText fontSize='1.375rem' fontWeight={600}>
                {formatAvailability(platform.apiChecksPassed)}
              </BodyText>
            </HeadlineRow>
          </MetricCard>

          <MetricCard>
            <CardHeaderRow>
              <IconBox>
                <TravelExplore fontSize='small' />
              </IconBox>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                <BodyText fontSize='0.8125rem' fontWeight={500} noWrap>
                  Search &amp; upload checks
                </BodyText>
                <DetailText color='text.secondary'>Synthetic canaries, subset</DetailText>
              </Box>
            </CardHeaderRow>
            <HeadlineRow>
              <BodyText fontSize='1.375rem' fontWeight={600}>
                {formatAvailability(platform.searchChecksPassed)}
              </BodyText>
            </HeadlineRow>
          </MetricCard>
        </Box>
      </Box>
    </Box>
  );
};

// --- Main Tile Component ---

export const SystemHealthTile = () => {
  const [days, setDays] = useState<HealthPeriodDays>(DEFAULT_HEALTH_PERIOD);
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { data, isLoading, isError } = useGetSystemHealth(tenantId, days);

  const handlePeriodChange = (event: SelectChangeEvent<HealthPeriodDays>) => {
    setDays(Number(event.target.value) as HealthPeriodDays);
  };

  const periodSelect = (
    <FormControl size='small' variant='standard'>
      <Select<HealthPeriodDays>
        value={days}
        onChange={handlePeriodChange}
        data-testid='system-health-period-select'
        inputProps={{ 'aria-label': 'System health period' }}
      >
        {HEALTH_PERIOD_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value} data-testid={`system-health-period-${option.value}`}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const headerActions = (
    <>
      <InternalOnlyBadge />
      {periodSelect}
    </>
  );

  if (!tenantId || isLoading) {
    return (
      <DashboardTile id='system-health' title='System Health' icon={<MonitorHeart />} action={headerActions}>
        <BodyText color='text.secondary'>Loading health metrics...</BodyText>
      </DashboardTile>
    );
  }

  if (isError || !data) {
    return (
      <DashboardTile id='system-health' title='System Health' icon={<MonitorHeart />} action={headerActions}>
        <BodyText color='text.error'>Failed to load system health.</BodyText>
      </DashboardTile>
    );
  }

  const hasNoScores = data.platform.scoredDays === 0;

  // No canary score across the widest window means this stack has no feed, so there is nothing
  // honest to show. Saying nothing beats rendering zeroes that would read as an outage.
  if (hasNoScores && days === DEFAULT_HEALTH_PERIOD) {
    return;
  }

  return (
    <DashboardTile id='system-health' title='System Health' icon={<MonitorHeart />} action={headerActions}>
      {hasNoScores && <BodyText color='text.secondary'>No canary scores in the last {days} days.</BodyText>}
      {!hasNoScores && <SystemHealthContent data={data} days={days} />}
    </DashboardTile>
  );
};
